import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, canWrite, isSuperAdmin } from "@/lib/auth";
import { toast } from "sonner";
import { InlineDeleteButton } from "@/components/InlineDeleteButton";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Save, Trash2, Plus, CheckCircle2, ShieldAlert, Rocket,
  ExternalLink, AlertTriangle,
} from "lucide-react";
import { ReleaseStatusBadge } from "./ReleaseIntelDesk";

export default function ReleaseIntelEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [linked, setLinked] = useState(null);
  const [vocab, setVocab] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("planning");
  const [linkedTracksInput, setLinkedTracksInput] = useState("");
  const [linkedSyncInput, setLinkedSyncInput] = useState("");
  const [linkedCommInput, setLinkedCommInput] = useState("");
  const [blockerLabel, setBlockerLabel] = useState("");
  const [blockerDetail, setBlockerDetail] = useState("");
  const [blockerCategory, setBlockerCategory] = useState("other");
  const mayWrite = canWrite(user);

  const load = useCallback(async () => {
    try {
      const [d, ls, v] = await Promise.all([
        api.get(`/release-intel/${id}`),
        api.get(`/release-intel/${id}/linked-status`),
        api.get("/release-intel/meta/vocab"),
      ]);
      setDoc(d.data);
      setLinked(ls.data);
      setVocab(v.data || {});
      setLinkedTracksInput((d.data?.linked_track_ids || []).join(", "));
      setLinkedSyncInput((d.data?.linked_sync_brief_ids || []).join(", "));
      setLinkedCommInput((d.data?.linked_communication_ids || []).join(", "));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const persist = async (patch, { silent = false, refreshLinked = true } = {}) => {
    if (!doc) return null;
    setSaving(true);
    try {
      const r = await api.patch(`/release-intel/${id}`, patch);
      setDoc(r.data);
      if (refreshLinked) {
        const ls = await api.get(`/release-intel/${id}/linked-status`);
        setLinked(ls.data);
      }
      if (!silent) toast.success("Saved.");
      return r.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error) {
        toast.error(detail.error);
      } else {
        toast.error(detail || "Save failed.");
      }
      throw err;
    } finally { setSaving(false); }
  };

  const applyLinks = async () => {
    const parse = (s) => (s || "").split(",").map((x)=>x.trim()).filter(Boolean);
    await persist({
      linked_track_ids: parse(linkedTracksInput),
      linked_sync_brief_ids: parse(linkedSyncInput),
      linked_communication_ids: parse(linkedCommInput),
    });
  };

  const addBlocker = async () => {
    if (!blockerLabel.trim()) { toast.error("Blocker label required."); return; }
    try {
      const r = await api.post(`/release-intel/${id}/blockers`, {
        label: blockerLabel.trim(),
        detail: blockerDetail.trim(),
        category: blockerCategory,
      });
      setDoc(r.data);
      setBlockerLabel(""); setBlockerDetail(""); setBlockerCategory("other");
      toast.success("Blocker opened.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add blocker failed.");
    }
  };

  const resolveBlocker = async (bid) => {
    const resolution = window.prompt("Enter resolution note (required):");
    if (!resolution || !resolution.trim()) return;
    try {
      const r = await api.post(`/release-intel/${id}/blockers/${bid}/resolve`, { resolution: resolution.trim() });
      setDoc(r.data);
      toast.success("Blocker resolved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resolve failed.");
    }
  };

  const remove = async () => {
    if (!isSuperAdmin(user)) return;
    if (!window.confirm("Delete this Release Intelligence record permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/release-intel/${id}`);
      toast.success("Deleted.");
      navigate("/advanced/release-intel");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  if (!doc) {
    return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  }
  if (!mayWrite) {
    return <div className="p-8 text-sm text-muted-foreground">You do not have permission to view this workspace.</div>;
  }

  const openBlockers = (doc.blockers || []).filter((b) => !b.resolved_at);
  const resolvedBlockers = (doc.blockers || []).filter((b) => !!b.resolved_at);

  return (
    <div className="p-6 space-y-6" data-testid="release-intel-editor">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" asChild data-testid="ri-back">
          <Link to="/advanced/release-intel"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Link>
        </Button>
        <Rocket className="w-6 h-6 text-primary" />
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs opacity-70">{doc.internal_reference}</span>
            <ReleaseStatusBadge value={doc.status} />
          </div>
          <h1 className="text-xl font-semibold">{doc.title || "(untitled release)"}</h1>
        </div>
        <div className="flex-1" />
        {isSuperAdmin(user) && (
          <InlineDeleteButton
            endpoint={`/release-intel/${id}`}
            recordLabel="Release Intelligence record"
            recordId={id}
            isSuperAdmin={true}
            variant="outline"
            showLabel={true}
            onDeleted={() => navigate("/advanced/release-intel")}
            testid="ri-delete"
          />
        )}
      </div>

      {linked?.has_blocked_pathway && (
        <Card style={{ borderColor: "hsl(0 55% 72%)", background: "hsl(0 55% 97%)" }} data-testid="ri-blocked-warn">
          <CardContent className="py-3 flex items-center gap-2 text-sm" style={{ color: "hsl(0 65% 30%)" }}>
            <ShieldAlert className="w-4 h-4" />
            One or more linked tracks are on a blocked pathway (DEMO_ONLY, DEVELOPMENT_CANDIDATE, or CLEARANCE_REVIEW). Release-ready claims are refused by the server until this is resolved.
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="ri-tabs">
          <TabsTrigger value="planning" data-testid="ri-tab-planning">Planning</TabsTrigger>
          <TabsTrigger value="links" data-testid="ri-tab-links">Links &amp; Linked Status</TabsTrigger>
          <TabsTrigger value="blockers" data-testid="ri-tab-blockers">Blockers ({openBlockers.length})</TabsTrigger>
          <TabsTrigger value="outcome" data-testid="ri-tab-outcome">Outcome</TabsTrigger>
        </TabsList>

        <TabsContent value="planning" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Release / campaign details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Input value={doc.title || ""} onChange={(e)=>setDoc({...doc, title: e.target.value})} onBlur={()=>persist({ title: doc.title }, { silent: true })} data-testid="ri-edit-title" />
              </div>
              <div>
                <Label>Internal reference</Label>
                <Input value={doc.internal_reference || ""} onChange={(e)=>setDoc({...doc, internal_reference: e.target.value})} onBlur={()=>persist({ internal_reference: doc.internal_reference }, { silent: true })} />
              </div>
              <div className="md:col-span-2">
                <Label>Release objective</Label>
                <Textarea rows={3} value={doc.release_objective || ""} onChange={(e)=>setDoc({...doc, release_objective: e.target.value})} onBlur={()=>persist({ release_objective: doc.release_objective }, { silent: true })} />
              </div>
              <div>
                <Label>Target date / window start</Label>
                <Input value={doc.target_date || ""} onChange={(e)=>setDoc({...doc, target_date: e.target.value})} onBlur={()=>persist({ target_date: doc.target_date }, { silent: true })} />
              </div>
              <div>
                <Label>Target window end</Label>
                <Input value={doc.target_window_end || ""} onChange={(e)=>setDoc({...doc, target_window_end: e.target.value})} onBlur={()=>persist({ target_window_end: doc.target_window_end }, { silent: true })} />
              </div>
              <div>
                <Label>Territory</Label>
                <Input value={doc.territory || ""} onChange={(e)=>setDoc({...doc, territory: e.target.value})} onBlur={()=>persist({ territory: doc.territory }, { silent: true })} />
              </div>
              <div>
                <Label>Sync relevance</Label>
                <Input value={doc.sync_relevance || ""} onChange={(e)=>setDoc({...doc, sync_relevance: e.target.value})} onBlur={()=>persist({ sync_relevance: doc.sync_relevance }, { silent: true })} placeholder="high / medium / low / none" />
              </div>
              <div>
                <Label>Internal owner (email)</Label>
                <Input value={doc.internal_owner_email || ""} onChange={(e)=>setDoc({...doc, internal_owner_email: e.target.value})} onBlur={()=>persist({ internal_owner_email: doc.internal_owner_email }, { silent: true })} />
              </div>
              <div>
                <Label>Internal owner (name)</Label>
                <Input value={doc.internal_owner_name || ""} onChange={(e)=>setDoc({...doc, internal_owner_name: e.target.value})} onBlur={()=>persist({ internal_owner_name: doc.internal_owner_name }, { silent: true })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={doc.status} onValueChange={(v)=>persist({ status: v })}>
                  <SelectTrigger data-testid="ri-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(vocab.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Readiness state</Label>
                <Select value={doc.readiness_state} onValueChange={(v)=>persist({ readiness_state: v })}>
                  <SelectTrigger data-testid="ri-edit-readiness"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(vocab.readiness_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Next action</Label>
                <Textarea rows={2} value={doc.next_action || ""} onChange={(e)=>setDoc({...doc, next_action: e.target.value})} onBlur={()=>persist({ next_action: doc.next_action }, { silent: true })} />
              </div>
              <div className="md:col-span-2">
                <Label>Campaign / release notes</Label>
                <Textarea rows={3} value={doc.campaign_notes || ""} onChange={(e)=>setDoc({...doc, campaign_notes: e.target.value})} onBlur={()=>persist({ campaign_notes: doc.campaign_notes }, { silent: true })} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="links" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Linked records</CardTitle>
              <CardDescription>Kandake stays the source of truth. This tab shows a controlled reference and a live linked-status view.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Linked track IDs (comma-separated)</Label>
                <Textarea rows={2} value={linkedTracksInput} onChange={(e)=>setLinkedTracksInput(e.target.value)} data-testid="ri-linked-tracks" />
              </div>
              <div>
                <Label>Linked sync brief IDs</Label>
                <Textarea rows={2} value={linkedSyncInput} onChange={(e)=>setLinkedSyncInput(e.target.value)} data-testid="ri-linked-sync" />
              </div>
              <div>
                <Label>Linked communication IDs</Label>
                <Textarea rows={2} value={linkedCommInput} onChange={(e)=>setLinkedCommInput(e.target.value)} data-testid="ri-linked-comms" />
              </div>
              <Button onClick={applyLinks} disabled={saving} className="gap-2" data-testid="ri-save-links">
                <Save className="w-4 h-4" /> Save links
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Live linked-status</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <div className="font-semibold mb-1">Tracks ({(linked?.linked_tracks || []).length})</div>
                {(linked?.linked_tracks || []).length === 0 ? (
                  <div className="text-muted-foreground">No tracks linked.</div>
                ) : (
                  <ul className="space-y-2">
                    {(linked?.linked_tracks || []).map((t) => (
                      <li key={t.id} className="border border-border rounded-md px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/tracks/${t.id}`} className="font-medium underline">{t.track_title || t.id}</Link>
                          <span className="text-xs opacity-70">{t.artist}</span>
                          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{t.asset_pathway || "—"}</Badge>
                          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{t.clearance_status || "—"}</Badge>
                          {t.attorney_review_required && (
                            <Badge variant="outline" className="text-[10px]" style={{ background: "hsl(0 55% 94%)", color: "hsl(0 65% 30%)", borderColor: "hsl(0 55% 72%)" }}>Attorney review</Badge>
                          )}
                          {t.open_compliance_task_count > 0 && (
                            <Badge variant="outline" className="text-[10px]">{t.open_compliance_task_count} open task(s)</Badge>
                          )}
                          {t.loc_prep?.status && <span className="text-xs opacity-70">· LOC: {t.loc_prep.status}</span>}
                          {t.artwork_review?.outcome && <span className="text-xs opacity-70">· Artwork: {t.artwork_review.outcome}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="font-semibold mb-1">Sync briefs ({(linked?.linked_sync_briefs || []).length})</div>
                {(linked?.linked_sync_briefs || []).length === 0 ? (
                  <div className="text-muted-foreground">No sync briefs linked.</div>
                ) : (
                  <ul className="space-y-1">
                    {(linked?.linked_sync_briefs || []).map((s) => (
                      <li key={s.id} className="flex items-center gap-2">
                        <Link to={`/advanced/sync-command-desk/${s.id}`} className="underline font-mono text-xs">{s.internal_brief_id || s.id}</Link>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{s.status || "—"}</Badge>
                        {s.media_type && <span className="text-xs opacity-70">· {s.media_type}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="font-semibold mb-1">Communications ({(linked?.linked_communications || []).length})</div>
                {(linked?.linked_communications || []).length === 0 ? (
                  <div className="text-muted-foreground">No communications linked.</div>
                ) : (
                  <ul className="space-y-1">
                    {(linked?.linked_communications || []).map((c) => (
                      <li key={c.id} className="flex items-center gap-2">
                        <Link to={"/advanced/communications"} className="underline text-xs">{c.internal_reference || c.id}</Link>
                        {c.classification && <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{c.classification}</Badge>}
                        {c.status && <span className="text-xs opacity-70">· {c.status}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blockers" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Open blockers ({openBlockers.length})</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {openBlockers.length === 0 ? (
                <div className="text-sm text-muted-foreground flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> No open blockers.</div>
              ) : (
                <ul className="space-y-2">
                  {openBlockers.map((b) => (
                    <li key={b.id} className="border border-border rounded-md px-3 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        <span className="font-medium text-sm">{b.label}</span>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{b.category}</Badge>
                        <span className="text-xs opacity-70">opened {b.opened_at} by {b.opened_by}</span>
                        <div className="flex-1" />
                        <Button variant="outline" size="sm" onClick={()=>resolveBlocker(b.id)} data-testid={`ri-blocker-resolve-${b.id}`}>Resolve</Button>
                      </div>
                      {b.detail && <div className="text-xs mt-1 whitespace-pre-wrap">{b.detail}</div>}
                    </li>
                  ))}
                </ul>
              )}
              <div className="pt-2 border-t border-border">
                <div className="font-semibold text-sm mb-2">Add a blocker</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Label</Label>
                    <Input value={blockerLabel} onChange={(e)=>setBlockerLabel(e.target.value)} data-testid="ri-blocker-label" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={blockerCategory} onValueChange={setBlockerCategory}>
                      <SelectTrigger data-testid="ri-blocker-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["rights","compliance","artwork","loc","delivery","scheduling","other"].map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={addBlocker} className="w-full gap-2" data-testid="ri-blocker-add"><Plus className="w-4 h-4" /> Add</Button>
                  </div>
                  <div className="md:col-span-3">
                    <Label>Detail</Label>
                    <Textarea rows={2} value={blockerDetail} onChange={(e)=>setBlockerDetail(e.target.value)} data-testid="ri-blocker-detail" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {resolvedBlockers.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Resolved blockers ({resolvedBlockers.length})</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {resolvedBlockers.map((b) => (
                    <li key={b.id} className="flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span className="font-medium">{b.label}</span>
                      <span className="text-xs opacity-70">— {b.resolution} ({b.resolved_by} on {b.resolved_at})</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="outcome" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Outcome / learning notes</CardTitle></CardHeader>
            <CardContent>
              <Textarea rows={8} value={doc.outcome_notes || ""} onChange={(e)=>setDoc({...doc, outcome_notes: e.target.value})} onBlur={()=>persist({ outcome_notes: doc.outcome_notes }, { silent: true })} data-testid="ri-outcome-notes" />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
